"use client";

import { useSearchParams } from "next/navigation";
import { useState } from "react";

import { Btn, Card, CardContent, CardHeader, CardTitle, Input } from "@/components/ui";
import { BASE } from "@/lib/api/client";

function LoginSpinner() {
  return (
    <svg
      className="h-3.5 w-3.5 animate-spin"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

/** Only in-app paths; blocks open redirects like ``//evil.com``. */
function safePostLoginPath(raw: string | null): string {
  if (!raw || !raw.startsWith("/") || raw.startsWith("//")) return "/";
  const path = raw.split("?")[0] ?? "/";
  return path.startsWith("/") && !path.startsWith("//") ? path : "/";
}

export function LoginForm() {
  const searchParams = useSearchParams();
  const next = safePostLoginPath(searchParams.get("next"));

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      const r = await fetch(`${BASE}/auth/login`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      if (!r.ok) {
        const body = (await r.json().catch(() => ({}))) as { detail?: string };
        setError(
          typeof body.detail === "string" ? body.detail : "Credenciales inválidas",
        );
        return;
      }
      // Full navigation so middleware and RSC see the new httpOnly cookies.
      // ``router.push`` after login often left users on /login (cookies not on the next flight).
      window.location.assign(next);
    } catch {
      setError("No se pudo conectar con el servidor");
    } finally {
      setPending(false);
    }
  }

  return (
    <Card className="w-full max-w-md shadow-lg border-slate-200 dark:border-slate-800">
      <CardHeader>
        <CardTitle className="text-xl">Iniciar sesión</CardTitle>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          Ingresá con tu cuenta del backoffice.
        </p>
      </CardHeader>
      <CardContent>
        <form
          onSubmit={onSubmit}
          className="flex flex-col gap-4"
          aria-busy={pending}
        >
          {error ? (
            <p className="text-sm text-red-600 dark:text-red-400" role="alert">
              {error}
            </p>
          ) : null}
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
              Email
            </label>
            <Input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
              Contraseña
            </label>
            <Input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <Btn
            type="submit"
            className="w-full justify-center"
            disabled={pending}
            icon={pending ? <LoginSpinner /> : undefined}
          >
            {pending ? "Entrando…" : "Entrar"}
          </Btn>
        </form>
      </CardContent>
    </Card>
  );
}
